import React, { useState, useMemo } from 'react';
import { Link, router } from '@inertiajs/react';
import Layout from '../../../components/Layout.jsx';

const statusBadge = {
  assigned:  'bg-blue-50 text-blue-700 border-blue-200',
  accessed:  'bg-amber-50 text-amber-700 border-amber-200',
  submitted: 'bg-green-50 text-green-700 border-green-200',
};

export default function SuperadminReviewersShow({ reviewer, assignments = [], available_courses = [] }) {
  const [query, setQuery] = useState('');
  const [submittingId, setSubmittingId] = useState(null);
  const [assigningCourseId, setAssigningCourseId] = useState(null);
  const [loginCopied, setLoginCopied] = useState(false);

  const handleCopyLogin = async () => {
    try {
      await navigator.clipboard.writeText(reviewer.login_url);
      setLoginCopied(true);
      setTimeout(() => setLoginCopied(false), 1500);
    } catch (_) {}
  };

  const handleRegenerate = () => {
    const ok = window.confirm(
      `Regenerate ${reviewer.name}'s login link?\n\nThe current link will stop working immediately. You'll need to share the new one with the reviewer.`
    );
    if (!ok) return;
    router.post(`/superadmin/reviewers/${reviewer.id}/regenerate_token`);
  };

  const filteredCourses = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return available_courses;
    return available_courses.filter((c) =>
      [c.name, c.role, c.status].filter(Boolean).some((v) => v.toLowerCase().includes(q))
    );
  }, [query, available_courses]);

  const handleAssign = (course) => {
    setAssigningCourseId(course.id);
    router.post(`/superadmin/reviewers/${reviewer.id}/assignments`,
      { admin_course_id: course.id },
      { onFinish: () => setAssigningCourseId(null) }
    );
  };

  const handleRemove = (assignment) => {
    const ok = window.confirm(`Remove "${assignment.admin_course.name}" from ${reviewer.name}?`);
    if (!ok) return;
    setSubmittingId(assignment.id);
    router.delete(`/superadmin/reviewers/${reviewer.id}/assignments/${assignment.id}`, {
      onFinish: () => setSubmittingId(null),
    });
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 sm:p-8 space-y-6">
        <div>
          <Link
            href="/superadmin/reviewers"
            className="text-sm text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            All reviewers
          </Link>
          <div className="mt-3 flex w-fit items-center gap-1.5 px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-200 text-xs font-medium">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Superadmin
          </div>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-gray-900">{reviewer.name}</h1>
          <p className="mt-1 text-sm text-gray-500">{reviewer.email}</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-lg font-semibold text-gray-900">Reviewer login link</h2>
          </div>
          <p className="text-sm text-gray-500 mb-3">
            Share this personal magic link with the reviewer. It logs them into their portal where they can see every course they've been assigned.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              readOnly
              value={reviewer.login_url}
              onFocus={(e) => e.target.select()}
              className="flex-1 font-mono text-xs px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-700"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleCopyLogin}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 transition cursor-pointer"
              >
                {loginCopied ? 'Copied' : 'Copy link'}
              </button>
              <button
                type="button"
                onClick={handleRegenerate}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-semibold rounded-lg text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 transition cursor-pointer"
              >
                Regenerate
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Assigned course reviews</h2>
            <span className="ml-auto text-sm text-gray-400">
              {assignments.length} {assignments.length === 1 ? 'assignment' : 'assignments'}
            </span>
          </div>

          {assignments.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">No assignments yet. Pick a course below.</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {assignments.map((a) => (
                <div key={a.id} className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 first:pt-0 last:pb-0">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900 truncate">{a.admin_course.name}</p>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${statusBadge[a.status] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                        {a.status}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Assigned {a.assigned_at}
                      {a.accessed_at && ` · Accessed ${a.accessed_at}`}
                      {a.submitted_at && ` · Submitted ${a.submitted_at}`}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => handleRemove(a)}
                      disabled={submittingId === a.id}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg text-red-700 bg-white hover:bg-red-50 border border-red-200 transition cursor-pointer disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Assign a course</h2>
            <span className="ml-auto text-sm text-gray-400">
              {available_courses.length} available
            </span>
          </div>

          <div className="relative mb-4">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
              </svg>
            </span>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search courses by name, role, or status…"
              className="w-full pl-10 pr-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>

          {filteredCourses.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">
              {available_courses.length === 0
                ? 'No unassigned courses available. Every course is already assigned to a reviewer.'
                : 'No courses match that search.'}
            </p>
          ) : (
            <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto pr-3">
              {filteredCourses.map((c) => (
                <div key={c.id} className="py-3 pl-1 flex items-center justify-between gap-3 first:pt-0 last:pb-0">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">{c.name}</p>
                    <p className="text-xs text-gray-500">
                      {c.role || '—'} · {c.status || 'no status'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleAssign(c)}
                    disabled={assigningCourseId === c.id}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 transition cursor-pointer disabled:opacity-50"
                  >
                    {assigningCourseId === c.id ? 'Assigning…' : 'Assign'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
